import React, { useState, useEffect, useRef } from 'react';
import Icon from './Icon';
import { onCallEvent, offCallEvent, hangUp, toggleMute } from '../data/twilio';
import { useStore } from '../data/store';
import { useToast } from './Toast';
import supabase from '../data/supabase';

export default function ActiveCallBar() {
  const store = useStore();
  const toast = useToast();
  const { contacts, deals, updateContact, addActivity, createTask, logCall, activities } = store;
  // Debounced contact-update queue: collapse rapid keystrokes into one save 500 ms
  // after the user stops typing. Per-contact-id buffer + timer.
  const contactSaveBufRef = useRef(new Map()); // id -> { updates: {}, timer }
  const flushContactSave = (id) => {
    const buf = contactSaveBufRef.current.get(id);
    if (!buf) return;
    contactSaveBufRef.current.delete(id);
    Promise.resolve(updateContact(id, buf.updates)).catch(err => {
      console.error("[ActiveCallBar] Failed to update contact:", err);
      toast?.error?.("Failed to update contact: " + (err?.message || "unknown error"));
    });
  };
  const queueContactSave = (id, updates) => {
    if (!id) return;
    const buf = contactSaveBufRef.current.get(id) || { updates: {}, timer: null };
    buf.updates = { ...buf.updates, ...updates };
    if (buf.timer) clearTimeout(buf.timer);
    buf.timer = setTimeout(() => flushContactSave(id), 500);
    contactSaveBufRef.current.set(id, buf);
  };
  // Flush on unmount so pending edits aren't lost
  useEffect(() => () => {
    contactSaveBufRef.current.forEach((_, id) => flushContactSave(id));
  }, []);
  const [phase, setPhase] = useState("idle"); // idle | dialing | connected | ended
  const [callTo, setCallTo] = useState("");
  const [contact, setContact] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState("");
  const [disposition, setDisposition] = useState("");
  const [saved, setSaved] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const finalElapsedRef = useRef(0);

  // Match a phone number to a contact
  const matchContact = (phoneNum) => {
    if (!phoneNum) return null;
    const clean = (phoneNum || "").replace(/\D/g, "");
    return contacts.find(c => {
      const cp = (c.ph || c.phone || "").replace(/\D/g, "");
      return cp && (cp === clean || clean.endsWith(cp) || cp.endsWith(clean));
    }) || null;
  };

  // Refs synced to current values for state read inside the call-event
  // handlers below. Without these, registering with [] deps would close
  // over stale state (or we'd be back to the leak-prone pattern of
  // re-registering on every state change). Mirrors PhoneWidget's pattern
  // post-67334f27.
  const phaseRef = useRef(phase);
  const contactRef = useRef(contact);
  const elapsedRef = useRef(elapsed);
  const matchContactRef = useRef(matchContact);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { contactRef.current = contact; }, [contact]);
  useEffect(() => { elapsedRef.current = elapsed; }, [elapsed]);
  useEffect(() => { matchContactRef.current = matchContact; }, [matchContact]);

  // Register Twilio call-event handlers ONCE on mount; remove on unmount.
  // Previous version had deps [contacts, elapsed, phase, contact] + empty
  // `return () => {}` cleanup — re-ran every elapsed tick (1Hz during a
  // call) and never removed prior generations. Same listener-leak shape
  // PhoneWidget had before commit 67334f27.
  useEffect(() => {
    const onDialing = (e) => {
      setCallTo(e.to);
      setPhase("dialing");
      setExpanded(false);
      setNotes("");
      setDisposition("");
      setSaved(false);
      setElapsed(0);
      startRef.current = null;
      setContact(matchContactRef.current(e.to));
    };
    const onConnected = (e) => {
      // If we were idle (inbound call accepted), set up the call state
      if (phaseRef.current === "idle" || !startRef.current) {
        const from = e?.from || e?.parameters?.From || "";
        if (from && !contactRef.current) {
          setCallTo(from);
          setContact(matchContactRef.current(from));
        }
        setNotes("");
        setDisposition("");
        setSaved(false);
      }
      setPhase("connected");
      startRef.current = Date.now();
      setExpanded(true);
    };
    const onEnded = () => {
      finalElapsedRef.current = elapsedRef.current;
      if (timerRef.current) clearInterval(timerRef.current);
      setPhase("ended");
      setExpanded(true);
    };
    const onMute = (e) => setMuted(e.muted);

    onCallEvent("dialing", onDialing);
    onCallEvent("connected", onConnected);
    onCallEvent("ended", onEnded);
    onCallEvent("mute", onMute);

    return () => {
      offCallEvent("dialing", onDialing);
      offCallEvent("connected", onConnected);
      offCallEvent("ended", onEnded);
      offCallEvent("mute", onMute);
    };
  }, []);

  useEffect(() => {
    if (phase === "connected" && startRef.current) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase]);

  // Load recent activities for matched contact
  useEffect(() => {
    if (!contact?.id) { setRecentActivities([]); return; }
    // Get from local store first
    const local = (activities || []).filter(a => a.contactId === contact.id).slice(0, 5);
    if (local.length > 0) { setRecentActivities(local); return; }
    // Fallback: query DB
    supabase.from("activities").select("*").eq("contact_id", contact.id)
      .order("created_at", { ascending: false }).limit(5)
      .then(({ data }) => { if (data) setRecentActivities(data); });
  }, [contact?.id]);

  const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60) < 10 ? "0" : ""}${s % 60}`;

  const handleSaveAndClose = () => {
    if (contact && (notes.trim() || disposition)) {
      logCall({
        contactId: contact.id,
        title: disposition ? `${disposition} — ${contact.fn} ${contact.ln}` : `Call — ${contact.fn} ${contact.ln}`,
        duration: finalElapsedRef.current || elapsed,
        disposition: disposition || "Connected",
        notes: notes.trim(),
      });
      addActivity({ type: "call", contactId: contact.id, title: disposition || "Call", notes: notes.trim() });
    }
    setSaved(true);
    setTimeout(() => { setPhase("idle"); setExpanded(false); setNotes(""); setDisposition(""); setContact(null); setElapsed(0); setSaved(false); }, 800);
  };

  const handleDismiss = () => { setPhase("idle"); setExpanded(false); setNotes(""); setDisposition(""); setContact(null); setElapsed(0); };

  if (phase === "idle") return null;

  // Don't show on Dialer page or dialer modal
  const hash = window.location.pathname?.slice(1) || "";
  if (hash === "dialer" || hash.startsWith("dialer")) return null;
  if (document.querySelector('[data-modal="dialer"]')) return null;

  const contactDeals = contact ? deals.filter(d => d.ct === contact.id) : [];
  const isEnded = phase === "ended";

  return (
    <>
      <style>{`@keyframes acbSlide{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}} @keyframes acbPulse{0%,100%{box-shadow:0 -2px 20px rgba(46,139,87,0.15)}50%{box-shadow:0 -2px 20px rgba(46,139,87,0.3)}}`}</style>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 5000, animation: "acbSlide 0.3s ease" }}>
        {/* Expanded Panel */}
        {expanded && contact && (
          <div style={{ maxWidth: 800, margin: "0 auto", marginBottom: -1, background: "#33363D", border: "1px solid rgba(217,219,221,0.08)", borderBottom: "none", borderRadius: "12px 12px 0 0", padding: "20px 24px" }}>
            <div style={{ display: "flex", gap: 20 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, fontWeight: 400, color: "rgba(217,219,221,0.4)", letterSpacing: ".08em", marginBottom: 10 }}>CONTACT INFO</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Company", key: "co", val: contact.co },
                    { label: "Title", key: "title", val: contact.title },
                    { label: "Email", key: "email", val: contact.email },
                    { label: "Lead Stage", key: "leadStage", val: contact.leadStage },
                    { label: "Score", key: "score", val: contact.score },
                    { label: "Status", key: "status", val: contact.status },
                  ].map((f, i) => (
                    <div key={i}>
                      <div style={{ fontSize: 9, color: "rgba(217,219,221,0.4)", marginBottom: 2 }}>{f.label}</div>
                      <input value={f.val || ""} onChange={e => { setContact(prev => ({ ...prev, [f.key]: e.target.value })); queueContactSave(contact.id, { [f.key]: e.target.value }); }}
                        onBlur={() => flushContactSave(contact.id)}
                        style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.04)", fontSize: 11, color: "#FAFAFA", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                    </div>
                  ))}
                </div>
                {contactDeals.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 9, color: "rgba(217,219,221,0.4)", letterSpacing: ".08em", marginBottom: 4 }}>ACTIVE DEALS</div>
                    {contactDeals.slice(0, 3).map(d => (
                      <div key={d.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", borderRadius: 6, background: "rgba(64,89,173,0.08)", marginBottom: 3, fontSize: 11 }}>
                        <span style={{ color: "#FAFAFA" }}>{d.name}</span>
                        <span style={{ color: "#4059AD" }}>${(d.val || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {recentActivities.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 9, color: "rgba(217,219,221,0.4)", letterSpacing: ".08em", marginBottom: 4 }}>RECENT ACTIVITY</div>
                    {recentActivities.slice(0, 4).map((a, i) => (
                      <div key={a.id || i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 6, background: "rgba(255,255,255,0.02)", marginBottom: 2, fontSize: 10, color: "rgba(217,219,221,0.5)" }}>
                        <span style={{ color: "#4059AD" }}>{a.type || "note"}</span>
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title || a.notes || "—"}</span>
                        <span style={{ flexShrink: 0, fontSize: 9 }}>{a.date || (a.created_at ? new Date(a.created_at).toLocaleDateString() : "")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, fontWeight: 400, color: "rgba(217,219,221,0.4)", letterSpacing: ".08em", marginBottom: 10 }}>CALL NOTES</div>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Type notes during the call..."
                  style={{ width: "100%", height: 80, padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.04)", fontSize: 11, color: "#FAFAFA", outline: "none", fontFamily: "inherit", resize: "none", boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                  {["Connected", "Follow up", "Not interested", "Voicemail", "No answer"].map(d => (
                    <button key={d} onClick={() => setDisposition(d)} style={{
                      padding: "4px 10px", borderRadius: 6, fontSize: 9, fontWeight: 400,
                      border: disposition === d ? "1px solid #4059AD" : "1px solid rgba(217,219,221,0.08)",
                      background: disposition === d ? "rgba(64,89,173,0.15)" : "transparent",
                      color: disposition === d ? "#FAFAFA" : "rgba(217,219,221,0.5)", cursor: "pointer", fontFamily: "inherit",
                    }}>{d}</button>
                  ))}
                </div>
                {isEnded ? (
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button onClick={handleSaveAndClose} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer", background: "#4059AD", color: "#D9DBDD", fontSize: 11, fontWeight: 400, fontFamily: "inherit" }}>{saved ? "Saved" : "Save & Close"}</button>
                    <button onClick={handleDismiss} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(217,219,221,0.5)", fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Dismiss</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button onClick={() => { if (contact) createTask({ title: `Follow up: ${contact.fn} ${contact.ln}`, contactId: contact.id, due: new Date(Date.now() + 86400000).toISOString().slice(0, 10), pri: "high" }); }}
                      style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "rgba(217,219,221,0.5)", fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Create Follow-up Task</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Bar */}
        <div style={{ background: "#2C2E33", borderTop: "1px solid rgba(217,219,221,0.08)", padding: "10px 24px", animation: !isEnded ? "acbPulse 3s infinite" : "none" }}>
          <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: isEnded ? "rgba(217,219,221,0.3)" : "#2E8B57", boxShadow: isEnded ? "none" : "0 0 8px rgba(46,139,87,0.5)" }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>
                  {contact ? `${contact.fn} ${contact.ln}` : callTo || "Calling..."}
                  {contact?.co && <span style={{ color: "rgba(217,219,221,0.4)" }}> · {contact.co}</span>}
                </div>
                <div style={{ fontSize: 11, color: "rgba(217,219,221,0.4)" }}>
                  {contact?.ph || callTo} · <span style={{ color: isEnded ? "rgba(217,219,221,0.5)" : "#2E8B57" }}>{isEnded ? `Call ended · ${fmtTime(finalElapsedRef.current || elapsed)}` : fmtTime(elapsed)}</span>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => setExpanded(!expanded)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: expanded ? "rgba(64,89,173,0.15)" : "transparent", color: expanded ? "#FAFAFA" : "rgba(217,219,221,0.5)", fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
                {expanded ? "Hide" : "Show Info"}
              </button>
              {!isEnded && (
                <>
                  <button onClick={() => toggleMute()} style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: muted ? "rgba(212,118,58,0.15)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    <Icon name={muted ? "mute" : "mic"} size={14} color={muted ? "#D4763A" : "rgba(217,219,221,0.5)"} />
                  </button>
                  <button onClick={() => hangUp()} style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#E53935", color: "#D9DBDD", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon name="phone" size={13} color="#D9DBDD" /> End
                  </button>
                </>
              )}
              {isEnded && (
                <button onClick={handleDismiss} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "transparent", color: "rgba(217,219,221,0.4)", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>×</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
